import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Tabs,
  Typography,
  message
} from "antd";
import {
  BankOutlined,
  MobileOutlined,
  SaveOutlined,
  GlobalOutlined,
  AndroidOutlined
} from "@ant-design/icons";
import axiosInstance from "../../../utils/axios";
import CustomUpload from "../../../component/customUpload";

const { Title, Text } = Typography;

const emptyData = {
  bank_qr: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  momo_qr: "",
  momo_account_name: "",
  momo_account_number: ""
};

function ChannelForm({ channel }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isApp = channel === "app";

  const loadSettings = async () => {
    setLoading(true);

    try {
      const res = await axiosInstance.get(
        `/payment-settings/${channel}`
      );

      form.setFieldsValue({
        ...emptyData,
        ...(res?.data?.data || {})
      });
    } catch (error) {
      console.error(error);
      message.error(
        isApp
          ? "Không thể tải thanh toán App!"
          : "Không thể tải thanh toán Website!"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [channel]);

  const onFinish = async (values) => {
    setSaving(true);

    try {
      const res = await axiosInstance.post(
        `/payment-settings/update/${channel}`,
        values
      );

      message.success(
        res?.data?.message || "Lưu thành công!"
      );

      if (res?.data?.data) {
        form.setFieldsValue(res.data.data);
      }
    } catch (error) {
      console.error(error);

      const msg =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        "Không thể cập nhật!";

      message.error(String(msg));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ minHeight: 360 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
    >
      <div
        style={{
          padding: "12px 16px",
          marginBottom: 20,
          borderRadius: 8,
          background: isApp ? "#f6ffed" : "#e6f4ff"
        }}
      >
        <strong>
          {isApp
            ? "Thông tin này chỉ dùng trong ứng dụng."
            : "Thông tin này chỉ dùng trên website."}
        </strong>
      </div>

      <Row gutter={[20, 20]}>
        <Col xs={24} xl={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <BankOutlined />
                <span>Thanh toán ngân hàng</span>
              </div>
            }
            className="shadow-sm"
            style={{ height: "100%" }}
          >
            <Form.Item
              label="Ảnh QR ngân hàng"
              name="bank_qr"
            >
              <CustomUpload
                type="image"
                accept=".png,.jpg,.jpeg,.jfif,.webp"
              />
            </Form.Item>

            <Form.Item
              label="Ngân hàng"
              name="bank_name"
            >
              <Input
                size="large"
                placeholder="Ví dụ: MB Bank, Vietcombank..."
              />
            </Form.Item>

            <Form.Item
              label="Tên chủ tài khoản"
              name="bank_account_name"
            >
              <Input
                size="large"
                placeholder="Tên chủ tài khoản"
              />
            </Form.Item>

            <Form.Item
              label="Số tài khoản"
              name="bank_account_number"
            >
              <Input
                size="large"
                placeholder="Số tài khoản ngân hàng"
              />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} xl={12}>
          <Card
            title={
              <div className="flex items-center gap-2">
                <MobileOutlined />
                <span>Thanh toán MoMo</span>
              </div>
            }
            className="shadow-sm"
            style={{ height: "100%" }}
          >
            <Form.Item
              label="Ảnh QR MoMo"
              name="momo_qr"
            >
              <CustomUpload
                type="image"
                accept=".png,.jpg,.jpeg,.jfif,.webp"
              />
            </Form.Item>

            <Form.Item
              label="Tên chủ tài khoản"
              name="momo_account_name"
            >
              <Input
                size="large"
                placeholder="Tên chủ tài khoản MoMo"
              />
            </Form.Item>

            <Form.Item
              label="Số tài khoản / số điện thoại"
              name="momo_account_number"
            >
              <Input
                size="large"
                placeholder="Số điện thoại MoMo"
              />
            </Form.Item>
          </Card>
        </Col>
      </Row>

      <div className="mt-5 flex justify-end">
        <Button
          type="primary"
          htmlType="submit"
          size="large"
          loading={saving}
          icon={<SaveOutlined />}
        >
          {isApp
            ? "Lưu thanh toán App"
            : "Lưu thanh toán Website"}
        </Button>
      </div>
    </Form>
  );
}

export default function PaymentSettings() {
  return (
    <div className="p-2 md:p-4">
      <div className="mb-5">
        <Title level={2} style={{ marginBottom: 4 }}>
          Tài khoản thanh toán
        </Title>

        <Text type="secondary">
          Website và ứng dụng sử dụng hai cấu hình thanh toán độc lập.
        </Text>
      </div>

      <Card className="shadow-sm">
        <Tabs
          defaultActiveKey="web"
          size="large"
          items={[
            {
              key: "web",
              label: (
                <span>
                  <GlobalOutlined /> Website
                </span>
              ),
              children: <ChannelForm channel="web" />
            },
            {
              key: "app",
              label: (
                <span>
                  <AndroidOutlined /> Ứng dụng
                </span>
              ),
              children: <ChannelForm channel="app" />
            }
          ]}
        />
      </Card>
    </div>
  );
}
